import { useEffect, useState } from "react";

import { useLazyGetEditClipsQuery } from "../../redux/slices";
import { SynergyClip, SynergyEdit } from "../../redux/slices/synergy-api/types";
import { formatDate, formatDurationMilliseconds, formatDurationSeconds, getTeamFromClip } from "../../utils";
import { Row } from "../table/table";

export interface useSynergyClipManagerProps {
  uploadClips: (clips: SynergyClip[]) => void;
  edits: SynergyEdit[];
  onClose: () => void;
}

export const useSynergyClipManager = ({ uploadClips, edits, onClose }: useSynergyClipManagerProps) => {
  const [selectedEdit, setSelectedEdit] = useState<Row | null>(null);
  const [clips, setClips] = useState<SynergyClip[] | null>(null);
  const [clipsToAdd, setClipsToAdd] = useState<SynergyClip[]>([]);
  const [clipsToRemove, setClipsToRemove] = useState<SynergyClip[]>([]);
  const [clipsToAttach, setClipsToAttach] = useState<SynergyClip[]>([]);
  const [selectedClips, setSelectedClips] = useState<SynergyClip[]>([]);
  const [clipsActionTaken, setClipsActionTaken] = useState(false);
  const [playActionTaken, setPlayActionTaken] = useState(false);

  const [getEditClips] = useLazyGetEditClipsQuery();

  useEffect(() => {
    const actionTaken = clipsToAdd.length === 0 && clipsToRemove.length === 0;

    setClipsActionTaken(actionTaken);
  }, [clipsToAdd, clipsToRemove]);

  useEffect(() => {
    const actionTaken = clipsToAttach.length === 0;

    setPlayActionTaken(actionTaken);
  }, [clipsToAttach]);

  useEffect(() => {
    let selectedClips = [...clipsToAdd, ...clipsToAttach];
    // remove clipsToRemove from selectedClips
    selectedClips = selectedClips.filter((clip) => !clipsToRemove.find((c) => c.id === clip.id));

    setSelectedClips(selectedClips);
  }, [clipsToAdd, clipsToAttach, clipsToRemove]);

  useEffect(() => {
    const fetchEditClips = async () => {
      if (selectedEdit) {
        const currentEdit = edits.find((edit) => edit.id === selectedEdit.id);
        const clips = await await getEditClips({
          editId: currentEdit?.id || "",
          clipCount: currentEdit?.clipCount || 0,
        }).unwrap();
        setClips(clips);
      }
    };

    fetchEditClips();
  }, [edits, getEditClips, selectedEdit]);

  const handleClose = () => {
    setClips(null);
    setSelectedEdit(null);
    setClipsToAttach([]);
    setClipsToAdd([]);
    setClipsToRemove([]);
    onClose();
  };

  const handleSelectClip = (row: Row) => {
    // if the clip is alredy in clipsToAttach, add it to clipsToRemove
    const clip = clips?.find((clip) => clip.id === row.id);
    if (clipsToAttach.find((clip) => clip.id === row.id) && clip) {
      setClipsToRemove([...clipsToRemove, clip]);
    } else if (clip) {
      setClipsToAdd([...clipsToAdd, clip]);
    }

    // if the clip is in clipsToRemove, remove it
    if (clipsToRemove.find((clip) => clip.id === row.id)) {
      setClipsToRemove(clipsToRemove.filter((clip) => clip.id !== row.id));
    }
    // if the clip is in clipsToAdd, remove it
    if (clipsToAdd.find((clip) => clip.id === row.id)) {
      setClipsToAdd(clipsToAdd.filter((clip) => clip.id !== row.id));
    }
  };

  const handleCancelClipSelection = () => {
    setClipsToAdd([]);
    setClipsToRemove([]);
    setClips(null);
  };

  const handleFinishClipSelection = () => {
    // add clipsToAdd to clipsToAttach
    let newClipsToAttach = [...clipsToAdd, ...clipsToAttach];
    newClipsToAttach = newClipsToAttach.filter((clip) => !clipsToRemove.find((c) => c.id === clip.id));
    setClipsToAttach(newClipsToAttach);
    // reset state
    setClipsToRemove([]);
    setClipsToAdd([]);
    setClips(null);
  };

  const handleCancelAttachmentSelection = () => {
    setClipsToAttach([]);
    handleClose();
  };

  const handleFinishAttachmentSelection = () => {
    // attach clipsToAttach to play
    uploadClips(clipsToAttach);

    handleClose();
  };

  const handleSelectAll = () => {
    // if all clips are already selected, deselect all
    const selectedClips = [...clipsToAdd, ...clipsToAttach];
    const allSelected = clips?.every((clip) => selectedClips.find((c) => c.id === clip.id));
    if (allSelected) {
      setClipsToAdd([]);
      // set clips to remove to all the selectedClips found in clips
      const clipsToRemove = selectedClips.filter((clip) => clips?.find((c) => c.id === clip.id));
      setClipsToRemove(clipsToRemove);
    } else {
      // adding all clips not yet selected
      const clipsToAdd = clips?.filter((clip) => !clipsToAttach.find((c) => c.id === clip.id));
      setClipsToAdd(clipsToAdd || []);
      setClipsToRemove([]);
    }
  };

  const EditsToRows = (): Row[] => {
    return edits.map((edit) => {
      return {
        id: edit.id,
        columns: [
          {
            id: "title",
            value: edit.title,
          },
          {
            id: "creator",
            value: edit.creator,
          },
          {
            id: "created on",
            value: formatDate(edit.created),
          },
          {
            id: "modified on",
            value: formatDate(edit.updated),
          },
          {
            id: "total clips",
            value: edit.clipCount ? edit.clipCount.toString() : "0",
          },
          {
            id: "selected clips",
            value: clipsToAttach.filter((clip) => clip.editId === edit.id).length.toString(),
          },
        ],
      };
    });
  };

  interface ClipsToRowsProps {
    clips: SynergyClip[];
  }

  const ClipsToRows = ({ clips }: ClipsToRowsProps): Row[] => {
    return clips.map((clip) => {
      return {
        id: clip.id,
        columns: [
          {
            id: "title",
            value: clip.title,
          },
          {
            id: "description",
            value: clip.comments,
          },
          {
            id: "tags",
            value: clip.tags.join(",") || "N/A",
          },
          {
            id: "result",
            value: clip.result,
          },
          {
            id: "duration",
            value:
              clip.trimIn && clip.trimOut
                ? `${formatDurationMilliseconds(clip.trimOut - clip.trimIn)}`
                : clip.startIn && clip.startOut
                ? `${formatDurationMilliseconds(clip.startOut - clip.startIn)}`
                : "0",
          },
          {
            id: "in",
            value: clip.trimIn ? formatDurationMilliseconds(clip.trimIn) : "0",
          },
          {
            id: "out",
            value: clip.trimOut ? formatDurationMilliseconds(clip.trimOut) : "0",
          },
          {
            id: "team",
            value: getTeamFromClip(clip),
          },
          {
            id: "date",
            value: clip?.game?.date ? formatDate(clip.game.date) : "N/A",
          },
          {
            id: "period",
            value: clip.eventData.period ? clip.eventData.period.toString() : "N/A",
          },
          {
            id: "clock",
            value: clip.eventData.clock ? formatDurationSeconds(clip.eventData.clock) : "N/A",
          },
          {
            id: "player",
            value: clip.eventData.player?.name ? clip.eventData.player.name : "N/A",
          },
        ],
      };
    });
  };

  return {
    EditsToRows,
    ClipsToRows,
    handleSelectAll,
    handleSelectClip,
    handleCancelClipSelection,
    handleFinishClipSelection,
    handleCancelAttachmentSelection,
    handleFinishAttachmentSelection,
    setSelectedEdit,
    selectedClips,
    clips,
    handleClose,
    clipsActionTaken,
    playActionTaken,
    clipsToAttach,
  };
};
